import React from "react";
import { scoreTone, RISK_TONE, VERIF_TONE, monogramColor } from "../lib/format";
import { cn } from "../lib/utils";

export function Overline({ children, className }) {
  return <div className={cn("overline text-muted-foreground", className)}>{children}</div>;
}

export function Monogram({ symbol, name, size = 40, className }) {
  const s = (symbol || name || "?").slice(0, 3).toUpperCase();
  const color = monogramColor(name || symbol);
  return (
    <div
      className={cn("flex items-center justify-center font-heading font-bold text-white shrink-0", className)}
      style={{ width: size, height: size, background: color, borderRadius: 4, fontSize: size * 0.34 }}
    >
      {s}
    </div>
  );
}

export function ScorePill({ score, size = "md" }) {
  const t = scoreTone(score);
  const sz = size === "lg" ? "text-2xl px-3 py-1.5" : size === "sm" ? "text-xs px-1.5 py-0.5" : "text-sm px-2 py-1";
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-sm border font-mono font-bold tabular-nums", t.text, t.bg, t.border, sz)}>
      {score}
    </span>
  );
}

export function ScoreBar({ score, showValue = true }) {
  const t = scoreTone(score);
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="h-1.5 flex-1 bg-muted rounded-sm overflow-hidden">
        <div className="h-full rounded-sm transition-[width] duration-500" style={{ width: `${score}%`, background: t.bar }} />
      </div>
      {showValue && <span className={cn("num text-xs font-bold w-7 text-right", t.text)}>{score}</span>}
    </div>
  );
}

export function RiskBadge({ level, count }) {
  const t = RISK_TONE[level] || RISK_TONE.None;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 text-xs font-medium", t.text, t.bg, t.border)}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
      {level}{count != null ? ` · ${count}` : ""}
    </span>
  );
}

export function VerificationBadge({ status }) {
  const t = VERIF_TONE[status] || VERIF_TONE["Unverified"];
  return (
    <span className={cn("inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-medium", t.text, t.bg, t.border)}>
      {status}
    </span>
  );
}

export function ConfidenceBadge({ level }) {
  const map = {
    High: "text-emerald-700 bg-emerald-50 border-emerald-200",
    Medium: "text-amber-700 bg-amber-50 border-amber-200",
    Low: "text-slate-600 bg-slate-50 border-slate-200",
  };
  return (
    <span className={cn("inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-medium", map[level] || map.Low)}>
      {level} confidence
    </span>
  );
}

export function Tag({ children, className }) {
  return (
    <span className={cn("inline-flex items-center rounded-sm border border-border bg-muted/60 px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground", className)}>
      {children}
    </span>
  );
}

export function StatCard({ label, value, sub, icon: Icon, tone = "text-primary", testId }) {
  return (
    <div className="bg-card border border-border/60 p-4 hover:shadow-md transition-shadow duration-150 group" data-testid={testId}>
      <div className="flex items-start justify-between">
        <Overline>{label}</Overline>
        {Icon && <Icon className={cn("w-4 h-4 text-muted-foreground group-hover:scale-110 transition-transform duration-150", tone)} />}
      </div>
      <div className={cn("mt-2 font-heading font-bold text-2xl tabular-nums num", tone)}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export function SectionCard({ title, right, children, className, testId }) {
  return (
    <div className={cn("bg-card border border-border/60", className)} data-testid={testId}>
      {(title || right) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
          {title && <h3 className="font-heading font-bold text-sm tracking-tight text-foreground">{title}</h3>}
          {right}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function KV({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-border/40 last:border-0">
      <span className="overline text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{children ?? "—"}</span>
    </div>
  );
}

export function EmptyState({ title, sub, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && <Icon className="w-8 h-8 text-muted-foreground/50 mb-3" />}
      <div className="font-heading font-bold text-foreground">{title}</div>
      {sub && <div className="text-sm text-muted-foreground mt-1 max-w-md">{sub}</div>}
    </div>
  );
}

export function Disclaimer({ children, className }) {
  return (
    <div className={cn("text-[11px] text-muted-foreground border border-border/60 bg-muted/40 px-3 py-2 rounded-sm", className)}>
      {children}
    </div>
  );
}
